import {initializeTemplateEditors} from './TemplateEditor';

const initializedEditors = new WeakSet();

const suggestedNames = new WeakMap();

const renumberColumns = editor => {
    editor.querySelectorAll('[data-export-columns-list] [data-export-column-position]').forEach((position, index) => {
        position.textContent = index + 1;
    });
};

const syncAddButton = editor => {
    const columnCount    = editor.querySelectorAll('[data-export-columns-list] > [data-export-column]').length;
    const maximumColumns = Number.parseInt(editor.dataset.maximumColumns, 10);

    editor.querySelector('[data-export-columns-add]').disabled = columnCount >= maximumColumns;
};

const createColumnId = () => [...crypto.getRandomValues(new Uint8Array(4))]
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');

const addColumn = editor => {
    const template = editor.querySelector('template[data-export-column-template]');
    const list     = editor.querySelector('[data-export-columns-list]');

    list.append(template.content.cloneNode(true));
    list.lastElementChild.querySelector('[data-export-column-id]').value = createColumnId();
    renumberColumns(editor);
    syncAddButton(editor);
    editor.dispatchEvent(new CustomEvent('exportcolumnschanged'));
    list.lastElementChild.querySelector('od-select').open();
};

const removeColumn = (editor, column) => {
    column.remove();
    renumberColumns(editor);
    syncAddButton(editor);
    editor.dispatchEvent(new CustomEvent('exportcolumnschanged'));
};

const readColumn = column => ({
    id: column.querySelector('[data-export-column-id]').value,
    field: column.querySelector('[data-export-column-field] select').value,
    key: column.querySelector('[data-export-column-key]').value,
    arguments: [...column.querySelectorAll('[data-export-column-argument]')].map(argumentInput => argumentInput.value),
    dateTimeFormat: column.querySelector('[data-export-column-date-format="dateTime"] select').value,
    dateFormat: column.querySelector('[data-export-column-date-format="date"] select').value,
    template: column.querySelector('[data-export-twig-field]').value,
    name: column.querySelector('[data-export-column-name]').value,
});

const isBlankColumn = column => [
    column.querySelector('[data-export-column-field] select'),
    column.querySelector('[data-export-column-key]'),
    column.querySelector('[data-export-column-name]'),
    column.querySelector('[data-export-twig-field]'),
    ...column.querySelectorAll('[data-export-column-argument]'),
].every(input => input.value.trim() === '');

const dropBlankColumns = editor => {
    const columns      = [...editor.querySelectorAll('[data-export-columns-list] > [data-export-column]')];
    const blankColumns = columns.filter(isBlankColumn);
    const droppable    = blankColumns.length === columns.length ? blankColumns.slice(1) : blankColumns;

    if (droppable.length === 0) {
        return;
    }

    droppable.forEach(column => column.remove());
    renumberColumns(editor);
    syncAddButton(editor);
    editor.dispatchEvent(new CustomEvent('exportcolumnschanged'));
};

const showTitle = nameInput => {
    const title = nameInput.closest('[data-export-column]').querySelector('[data-export-column-title]');

    title.textContent = nameInput.value.trim();
};

const suggestName = (nameInput, suggestion) => {
    const currentName = nameInput.value.trim();

    if (currentName !== '' && currentName !== suggestedNames.get(nameInput)) {
        return;
    }

    nameInput.value = suggestion;
    suggestedNames.set(nameInput, suggestion);
    showTitle(nameInput);
};

const isListed = (fieldIds, fieldId) => fieldIds !== '' && fieldIds.split(',').includes(fieldId);

const showAvailability = (column, editor, fieldId) => {
    const isDisabled  = isListed(editor.dataset.disabledFields, fieldId);
    const isTurnedOff = isListed(editor.dataset.turnedOffFields, fieldId);
    const isDeleted   = isListed(editor.dataset.deletedFields, fieldId);

    column.classList.toggle('is-unavailable', isDisabled || isTurnedOff || isDeleted);
    column.querySelector('[data-export-column-unavailable="disabled"]').toggleAttribute('hidden', !isDisabled);
    column.querySelector('[data-export-column-unavailable="turnedOff"]').toggleAttribute('hidden', !isTurnedOff);
    column.querySelector('[data-export-column-unavailable="deleted"]').toggleAttribute('hidden', !isDeleted);
};

const showArguments = (column, parameterLabels) => {
    column.querySelectorAll('[data-export-column-argument]').forEach(argumentInput => {
        const position = Number.parseInt(argumentInput.dataset.exportColumnArgument, 10);
        const label    = parameterLabels[position];

        argumentInput.toggleAttribute('hidden', label === undefined);
        argumentInput.placeholder = label ?? '';

        if (label === undefined) {
            argumentInput.value = '';
        }
    });
};

export const initializeColumnEditors = root => {
    root.querySelectorAll('[data-export-columns]').forEach(editor => {
        if (initializedEditors.has(editor)) {
            return;
        }

        initializedEditors.add(editor);

        editor.addEventListener('sortabledrop', () => renumberColumns(editor));
    });
};

export const handleColumnEditorClick = event => {
    const editor = event.target.closest('[data-export-columns]');

    if (!editor) {
        return false;
    }

    if (event.target.closest('[data-export-columns-add]')) {
        addColumn(editor);

        return true;
    }

    const removeButton = event.target.closest('[data-export-column-remove]');

    if (!removeButton) {
        return false;
    }

    removeColumn(editor, removeButton.closest('[data-export-column]'));

    return true;
};

export const prepareColumnEditorSubmission = event => {
    if (event.detail.parameters.columns === undefined) {
        return;
    }

    const editor = document.querySelector('[data-export-columns]');

    event.detail.parameters.columns = JSON.stringify(
        [...editor.querySelectorAll('[data-export-columns-list] > [data-export-column]')].map(readColumn),
    );
};

export const handleColumnEditorInput = event => {
    const nameInput = event.target.closest('[data-export-column-name]');

    if (nameInput) {
        showTitle(nameInput);

        return;
    }

    const keyInput = event.target.closest('[data-export-column-key]');

    if (!keyInput) {
        return;
    }

    const column = keyInput.closest('[data-export-column]');

    suggestName(column.querySelector('[data-export-column-name]'), keyInput.value.trim());
};

document.addEventListener('wizard-step-changed', event => {
    const editor = event.detail.fromPanel?.querySelector('[data-export-columns]');

    if (!editor) {
        return;
    }

    dropBlankColumns(editor);
});

document.addEventListener('selectoptionschanged', event => {
    if (!event.selectContainer.matches('[data-export-column-field]')) {
        return;
    }

    const column        = event.selectContainer.closest('[data-export-column]');
    const editor        = column.closest('[data-export-columns]');
    const select        = event.selectContainer.querySelector('select');
    const keyInput      = column.querySelector('[data-export-column-key]');
    const nameInput     = column.querySelector('[data-export-column-name]');
    const templateBox   = column.querySelector('[data-export-column-twig]');
    const requiresKey   = editor.dataset.keyedFields.split(',').includes(select.value);
    const isDateTime    = editor.dataset.dateTimeFields.split(',').includes(select.value);
    const isDate        = editor.dataset.dateFields.split(',').includes(select.value);
    const isTemplate    = select.value === editor.dataset.templateField;

    column.querySelector('[data-export-column-date-format="dateTime"]').toggleAttribute('hidden', !isDateTime);
    column.querySelector('[data-export-column-date-format="date"]').toggleAttribute('hidden', !isDate);
    showArguments(column, JSON.parse(editor.dataset.parameterLabels)[select.value] ?? []);
    showAvailability(column, editor, select.value);
    keyInput.toggleAttribute('hidden', !requiresKey);
    templateBox.toggleAttribute('hidden', !isTemplate);

    if (isTemplate) {
        keyInput.value = '';
        initializeTemplateEditors(templateBox);
        requestAnimationFrame(() => templateBox.querySelector('[data-export-twig-field]').focus());

        return;
    }

    if (requiresKey) {
        suggestName(nameInput, keyInput.value.trim());
        requestAnimationFrame(() => keyInput.focus());

        return;
    }

    keyInput.value = '';
    suggestName(nameInput, select.selectedOptions[0]?.dataset.name ?? '');
});
